import React, { useState, useRef, useEffect } from 'react';
import { saveLocalTrack } from '../utils/localTrackStorage';

const LocalTrackUpload = ({ onTrackUploaded, onClose, activePlaylist, isJamSession, jamSessionId }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState([]);
  const fileInputRef = useRef(null);
  
  // Log what we received
  console.log('🔍 LocalTrackUpload mounted with:', { activePlaylist, isJamSession, jamSessionId });
  
  const savedPlaylistRef = useRef(activePlaylist); // Save playlist reference to prevent loss
  const savedJamSessionIdRef = useRef(jamSessionId); // Save jam session ID
  
  // Update refs if props change
  useEffect(() => {
    if (activePlaylist) {
      console.log('🔄 Updating savedPlaylistRef with:', activePlaylist);
      savedPlaylistRef.current = activePlaylist;
    }
    if (jamSessionId) {
      console.log('🔄 Updating savedJamSessionIdRef with:', jamSessionId);
      savedJamSessionIdRef.current = jamSessionId;
    }
  }, [activePlaylist, jamSessionId]);

  const handleFileSelect = async (files) => {
    if (!files || files.length === 0) return;

    setUploading(true);
    const results = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      
      // Check file type
      if (!file.type.startsWith('audio/')) {
        results.push({ filename: file.name, status: 'error', message: 'Not an audio file' });
        continue;
      }

      try {
        setUploadProgress(prev => [...prev, { filename: file.name, status: 'processing' }]);
        
        const track = await saveLocalTrack(file);
        
        // If in jam session context, add directly to jam session queue
        if (isJamSession && savedJamSessionIdRef.current) {
          console.log('🎵 Adding local track directly to jam session:', savedJamSessionIdRef.current);
          try {
            const axios = (await import('axios')).default;
            const token = localStorage.getItem('beatseek_token');
            const API = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
            
            await axios.post(
              `${API}/api/jam/${savedJamSessionIdRef.current}/queue`,
              {
                track_id: track.track_id,
                title: track.title,
                artist: track.artist,
                preview_url: null,
                spotify_uri: null,
                duration_ms: track.duration_ms || 0,
                artwork: track.artwork,
                source: 'local',
                local_id: track.id
              },
              { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            console.log('✅ Local track added to jam session queue!');
            results.push({ filename: file.name, status: 'success', track, addedToJam: true });
          } catch (error) {
            console.error('❌ Failed to add to jam session:', error);
            results.push({ filename: file.name, status: 'error', message: 'Failed to add to jam session' });
          }
        } else {
          // Regular playlist or discovery zone flow
          results.push({ filename: file.name, status: 'success', track });
          if (onTrackUploaded) {
            // Pass both track AND playlist info
            onTrackUploaded(track, savedPlaylistRef.current);
          }
        }
        
        setUploadProgress(prev => prev.map(p => 
          p.filename === file.name ? { ...p, status: 'success' } : p
        ));
      } catch (error) {
        results.push({ filename: file.name, status: 'error', message: error.message });
        setUploadProgress(prev => prev.map(p => 
          p.filename === file.name ? { ...p, status: 'error', message: error.message } : p
        ));
      }
    }

    setUploading(false);
    
    // Show summary
    const successCount = results.filter(r => r.status === 'success').length;
    const jamSuccessCount = results.filter(r => r.addedToJam).length;
    
    if (successCount > 0) {
      let message;
      if (isJamSession && jamSuccessCount > 0) {
        message = `✅ ${jamSuccessCount} track${jamSuccessCount > 1 ? 's' : ''} uploaded and added to jam session queue!`;
      } else if (activePlaylist) {
        message = `✅ ${successCount} track${successCount > 1 ? 's' : ''} uploaded and added to "${activePlaylist.name}"!`;
      } else {
        message = `✅ ${successCount} track${successCount > 1 ? 's' : ''} uploaded successfully!`;
      }
      
      alert(message);
      
      setTimeout(() => {
        if (onClose) onClose();
      }, 1000);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    handleFileSelect(e.dataTransfer.files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.85)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000000,
      backdropFilter: 'blur(10px)'
    }}>
      <div style={{
        background: 'linear-gradient(135deg, rgba(20, 20, 40, 0.98), rgba(30, 20, 50, 0.98))',
        borderRadius: '12px',
        padding: '2rem',
        maxWidth: '500px',
        width: '90%',
        border: '2px solid rgba(0, 255, 255, 0.3)',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)'
      }}>
        <h2 style={{ color: 'white', marginTop: 0 }}>📁 Upload Your Music</h2>
        
        {activePlaylist && (
          <div style={{
            background: 'rgba(0, 255, 255, 0.1)',
            border: '1px solid rgba(0, 255, 255, 0.3)',
            borderRadius: '8px',
            padding: '0.75rem',
            marginBottom: '1rem'
          }}>
            <p style={{ color: '#00ffff', margin: 0, fontSize: '0.9rem' }}>
              📝 Adding to: <strong>{activePlaylist.name}</strong>
            </p>
          </div>
        )}
        
        <p style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.9rem' }}>
          Add your own MP3/M4A files for full-length playback!
        </p>

        <div style={{
          background: 'rgba(255, 165, 0, 0.1)',
          border: '1px solid rgba(255, 165, 0, 0.3)',
          borderRadius: '8px',
          padding: '1rem',
          marginBottom: '1.5rem'
        }}>
          <p style={{ color: '#ffa500', margin: '0 0 0.5rem 0', fontWeight: '600' }}>
            ⚠️ Important:
          </p>
          <ul style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.85rem', margin: 0, paddingLeft: '1.5rem' }}>
            <li>Tracks stored temporarily in your browser</li>
            <li>Removed when you log out</li>
            <li>Maximum 10 tracks or 100MB per session</li>
            <li>For optimal performance</li>
          </ul>
        </div>

        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onClick={() => fileInputRef.current?.click()}
          style={{
            border: '2px dashed rgba(0, 255, 255, 0.4)',
            borderRadius: '8px',
            padding: '2rem',
            textAlign: 'center',
            cursor: 'pointer',
            background: 'rgba(0, 255, 255, 0.05)',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(0, 255, 255, 0.1)';
            e.currentTarget.style.borderColor = 'rgba(0, 255, 255, 0.6)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(0, 255, 255, 0.05)';
            e.currentTarget.style.borderColor = 'rgba(0, 255, 255, 0.4)';
          }}
        >
          <div style={{ fontSize: '3rem', marginBottom: '0.5rem' }}>🎵</div>
          <p style={{ color: 'white', fontSize: '1rem', fontWeight: '600', margin: '0 0 0.5rem 0' }}>
            Drag & Drop Files
          </p>
          <p style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.85rem', margin: 0 }}>
            or click to browse
          </p>
          <p style={{ color: 'rgba(0, 255, 255, 0.6)', fontSize: '0.75rem', marginTop: '0.5rem' }}>
            Supports: MP3, M4A, WAV, OGG, FLAC
          </p>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept="audio/*"
          multiple
          style={{ display: 'none' }}
          onChange={(e) => handleFileSelect(e.target.files)}
        />

        {uploadProgress.length > 0 && (
          <div style={{ marginTop: '1rem' }}>
            {uploadProgress.map((progress, index) => (
              <div key={index} style={{
                padding: '0.5rem',
                background: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '4px',
                marginBottom: '0.5rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <span style={{ color: 'white', fontSize: '0.85rem' }}>{progress.filename}</span>
                <span style={{ fontSize: '0.85rem' }}>
                  {progress.status === 'processing' && '⏳'}
                  {progress.status === 'success' && '✅'}
                  {progress.status === 'error' && '❌'}
                </span>
              </div>
            ))}
          </div>
        )}

        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1.5rem' }}>
          <button
            onClick={onClose}
            disabled={uploading}
            style={{
              flex: 1,
              padding: '0.75rem',
              background: 'rgba(255, 255, 255, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '6px',
              color: 'white',
              fontSize: '0.9rem',
              fontWeight: '600',
              cursor: uploading ? 'not-allowed' : 'pointer',
              opacity: uploading ? 0.5 : 1
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default LocalTrackUpload;
